import React, {Fragment, useEffect, useState} from 'react';
import {NavLink, useParams, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {fetchOrganization, fetchOrganizationContent, togglePinnedContent} from '../../slices/organizations.slice';
import {toast} from 'react-toastify';
import {formatBytes} from '../../components/DiskUsage';
import {Button, ButtonGroup, Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {ingestCPL, markContentDeleted, restoreContent} from '../../slices/content.slice';
import './OrganizationContent.scss'
import PageHeader from '../../components/PageHeader';
import {ContentGroupCard} from '../../components/ContentGroupCard';
import FiltersLabel from '../../components/FiltersLabel';
import MediablockStatusBar from '../../components/MediablockStatusBar';
import {canMarkForDeletion, canRestoreDeletion, getDeletionState} from '../../utils/contentDeletionState';
import {isMediablockConnected} from '../../utils/mediablock';
import {
    buildContentGroups,
    countStates,
    EMPTY_FILTERS,
    filterContentGroups,
    groupMatchesQuery,
    hasActiveFilters,
} from '../../utils/contentGroups';
import {formatTime} from '../../utils/dates';
import {isAdmin} from '../../utils/admin';

const formatMoment = date => date ? date.toLocaleString() : '';

// Mirrors bac-server CONTENT_REMOVAL_RESTORE_AFTER_HOURS (default 24): how long a removal the cleanup
// job gave up on stays visible before the server clears it by itself.
const AUTO_RESTORE_AFTER_HOURS = 24;
const MEDIABLOCK_OFFLINE_REASON = 'Mediablock offline — removals can be requested once it reconnects';
const MEDIABLOCK_OFFLINE_TRANSFER_REASON = 'Mediablock offline — transfers can be requested once it reconnects';

// Filter chips, in the order they appear. Each facet is OR within itself and AND with the others;
// a chip toggles in place, like the flags on the organizations list. `location` and `kind` values
// are the raw row values (`content.location`, clairmeta's ContentKind), `key` the three states the
// API already exposes to organization roles as `kdmStatus`.
const FILTER_CHIPS = [
    {facet: 'location', value: 'mediablock', label: 'Mediablock', icon: 'fa-cube'},
    {facet: 'location', value: 'BAC_server', label: 'BAC server', icon: 'fa-hard-drive'},
    {facet: 'kind', value: 'feature', label: 'Features', icon: 'fa-clapperboard'},
    {facet: 'kind', value: 'trailer', label: 'Trailers', icon: 'fa-film'},
    {facet: 'key', value: 'valid', label: 'Valid key', icon: 'fa-key'},
    {facet: 'key', value: 'expired', label: 'Expired key', icon: 'fa-calendar-xmark'},
    {facet: 'pinned', label: 'Pinned', icon: 'fa-thumbtack'},
];
// `state` has no chip of its own: the Mediablock status bar's lifecycle chips toggle it.
const LIST_FACETS = ['location', 'kind', 'key', 'state'];

// Deletion is a request, not an action: bac-app marks the DCP and the Mediablock cleanup job
// removes it later. A three-step timeline says that plainly — what was asked, where it is now,
// and that the row leaves this list once the Mediablock confirms.
const DeletionTimeline = ({deletion, showTechnical}) => {
    const outstanding = [
        deletion.lastAttemptAt && `Last attempt ${formatMoment(deletion.lastAttemptAt)}`,
        deletion.nextRetryAt && `Next attempt around ${formatTime(deletion.nextRetryAt)}`,
        showTechnical && deletion.error,
    ].filter(Boolean);

    return (
        <div className="cpl-timeline">
            <div className="cpl-step is-done">
                <span className="fas fa-check-circle cpl-step-icon"/>
                <div>
                    <div className="cpl-step-title">Deletion requested</div>
                    {deletion.requestedAt && (
                        <div className="cpl-step-meta">{formatMoment(deletion.requestedAt)}</div>
                    )}
                </div>
            </div>

            <div className={`cpl-step is-current text-${deletion.tone}`}>
                <span className={`fas ${deletion.icon} cpl-step-icon`}/>
                <div>
                    <div className="cpl-step-title">{deletion.label}</div>
                    <div className="cpl-step-meta">{deletion.detail}</div>
                    {outstanding.map(line => <div className="cpl-step-meta" key={line}>{line}</div>)}
                </div>
            </div>

            <div className="cpl-step">
                <span className="fas fa-circle cpl-step-icon"/>
                <div>
                    <div className="cpl-step-title">Removed from the Mediablock</div>
                    <div className="cpl-step-meta">
                        {deletion.needsAttention
                            ? `Will not happen on its own — restore the DCP and request the removal again once the Mediablock is connected. Requests left like this are restored automatically after ${AUTO_RESTORE_AFTER_HOURS} h.`
                            : 'This DCP leaves the list as soon as the Mediablock confirms the removal.'}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContentManager = () => {
    const {organizationId} = useParams();
    const dispatch = useDispatch();
    const token = useSelector(state => state.auth.token);
    const user = useSelector(state => state.auth.user);
    const organization = useSelector((state) => state.organizations.organization);
    const organizationContent = useSelector(state => state.organizations.organizationContent)

    const [searchParams, setSearchParams] = useSearchParams();
    const [modal, setModal] = useState({isOpen: false, content: null});

    // Search and filters live in the URL so a refresh (or a shared link) shows the same list.
    const parseURLFilters = () => ({
        search: searchParams.get('search') || '',
        ...Object.fromEntries(LIST_FACETS.map(facet => [facet, searchParams.get(facet)?.split(',').filter(Boolean) || []])),
        pinned: searchParams.get('pinned') === 'true',
    });
    const [filterBy, setFilterBy] = useState(parseURLFilters);

    const applyFilters = (newFilters) => {
        setFilterBy(newFilters);
        const params = new URLSearchParams();
        if (newFilters.search) params.set('search', newFilters.search);
        LIST_FACETS.filter(facet => newFilters[facet].length).forEach(facet => params.set(facet, newFilters[facet].join(',')));
        if (newFilters.pinned) params.set('pinned', 'true');
        setSearchParams(params);
    };

    const toggleChip = ({facet, value}) => {
        if (facet === 'pinned') return applyFilters({...filterBy, pinned: !filterBy.pinned});
        const selected = filterBy[facet];
        applyFilters({
            ...filterBy,
            [facet]: selected.includes(value) ? selected.filter(item => item !== value) : [...selected, value],
        });
    };

    const isChipActive = ({facet, value}) => facet === 'pinned' ? filterBy.pinned : filterBy[facet].includes(value);

    const clearFilters = () => applyFilters({search: '', ...EMPTY_FILTERS});

    const toggleContentModal = (content = null) => () => {
        setModal({isOpen: !modal.isOpen, content});
    };

    const handleSearch = (event) => {
        event.preventDefault();
        applyFilters({...filterBy, search: event.target.value});
    };

    const handleIngest = async (cpl) => {
        try {
            const promise = dispatch(ingestCPL(cpl)).unwrap();
            toast.promise(promise, {
                pending: 'Sending content to Mediablock...',
                success: 'Ingest started — transferring to Mediablock',
                error: {
                    render({ data }) {
                        return data?.message || 'Error updating content';
                    }
                },
            });

            // Keep the modal open and reflect the new "transferring" state in place (the redux list is
            // updated via the ingestCPL.fulfilled reducer), mirroring handleMarkDeleted.
            const updated = await promise;
            setModal(prev => ({ ...prev, content: updated }));
        } catch (error) {
            console.error('Ingest error:', error);
        }
    };

    const locationLabel = modal.content?.location === 'mediablock' ? 'Mediablock' : 'BAC server';
    const deletion = getDeletionState(modal.content);
    const mediablockOffline = !isMediablockConnected(organization?.mediablock);
    const hideKdmValidity = ['site-admin', 'site-user', 'site-guest'].includes(user?.role);
    const cplInfo = [
        {label: 'id', data: modal.content?._id},
        {label: 'type', data: modal.content?.type},
        {label: 'title', data: modal.content?.contentTitleText, wide: true},
        {label: 'cpl_uuid', data: modal.content?.cpl_uuid},
        {label: 'location', data: modal.content?.location},
        {label: `On ${locationLabel} since`, data: modal.content?.onMachine?.split('T')[0]}, //
        {label: 'File Size', data: formatBytes(modal.content?.fileSize)},
        {label: 'DCP Size', data: modal.content?.dcpSize ? formatBytes(modal.content?.dcpSize) : 'N/A'},


        {
            label: 'KDM',
            wide: true,
            data: hideKdmValidity ? {
                uuid: modal.content?.KDM_uuid || '',
                // Coarse, date-free key status for org roles (matches the card icon). Expired -> blank.
                status: modal.content?.kdmStatus === 'valid' ? 'Valid'
                    : modal.content?.kdmStatus === 'upcoming' ? 'Upcoming (not valid yet)'
                        : '',
            } : {
                uuid: modal.content?.KDM_uuid || '',
                from: modal.content?.KDM_notValidBefore?.split('T')[0] || '',
                until: modal.content?.KDM_notValidAfter?.split('T')[0] || '',
            },
        },
        {
            label: 'Deletion',
            panel: true,
            tone: deletion?.tone,
            condition: !!deletion,
            render: () => <DeletionTimeline deletion={deletion} showTechnical={isAdmin(user)}/>,
        },
        {
            label: 'Ingest status',
            panel: true,
            condition: !!(modal.content?.ingestRequestedAt || modal.content?.ingestFailedAt),
            data: (() => {
                const c = modal.content;
                if (!c) return {};
                const state = c.ingestFailedAt
                    ? 'Failed — retry from the button below'
                    : 'Transferring to Mediablock (waiting for mediablock to be online)';
                return {
                    state,
                    requestedAt: c.ingestRequestedAt ? new Date(c.ingestRequestedAt).toLocaleString() : '',
                    jobId: c.ingestJobId || '',
                    failedAt: c.ingestFailedAt ? new Date(c.ingestFailedAt).toLocaleString() : '',
                    error: c.ingestError || '',
                };
            })(),
        },
    ];

    // Data keys are camelCase ("lastAttemptAt"); split them so they stay readable once uppercased.
    const formatKey = key => key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toUpperCase();

    const renderField = field => {
        if (field.condition !== undefined && field.condition !== true) return null;

        // Grouped fields (KDM, the status blocks) hold an object; anything else — including a
        // missing value such as an absent onMachine date — renders as a single value.
        const isGroup = !!field.data && typeof field.data === 'object';
        // Empty sub-values (a KDM with no dates, a deletion that never failed) are dropped rather
        // than rendered as a dangling label with nothing next to it.
        const entries = isGroup
            ? Object.entries(field.data).filter(([, value]) => value !== '' && value !== null && value !== undefined)
            : [];

        return (
            <div
                className={`cpl-field${field.wide || field.panel ? ' cpl-wide' : ''}${field.panel ? ' cpl-panel' : ''}${field.tone ? ` cpl-panel-${field.tone}` : ''}`}
                key={field.label}
            >
                <div className="cpl-label">{field.label}</div>
                {field.render ? field.render() : isGroup ? (
                    entries.length ? (
                        <dl className="cpl-list">
                            {entries.map(([key, value]) => (
                                <Fragment key={key}>
                                    <dt>{formatKey(key)}</dt>
                                    <dd className={key === 'state' ? 'cpl-state' : undefined}>{`${value}`}</dd>
                                </Fragment>
                            ))}
                        </dl>
                    ) : (
                        <div className="cpl-empty">{`no ${field.label} available`}</div>
                    )
                ) : (
                    <div className={field.data ? 'cpl-value' : 'cpl-empty'}>
                        {field.data || `no ${field.label} available`}
                    </div>
                )}
            </div>
        );
    };

    const handleMarkDeleted = async () => {
        if (!window.confirm('Are you sure you want to mark this DCP for deletion from the Mediablock? This cannot be undone.')) return;
        const promise = dispatch(markContentDeleted(modal.content._id)).unwrap();
        toast.promise(promise, {
            pending: 'Marking DCP for deletion...',
            success: 'DCP marked for deletion',
            error: {
                render({ data }) {
                    return data?.message || 'Error marking content for deletion';
                }
            },
        });
        try {
            const updated = await promise;
            setModal(prevState => ({ ...prevState, content: updated }));
        } catch (error) {
            console.error('Mark deleted error:', error);
        }
    };

    // Clears a removal request nothing will ever finish. The DCP is still on the Mediablock (the
    // removal never happened), so the row simply reads as ordinary content again.
    const handleRestore = async () => {
        if (!window.confirm('Clear the removal request? The DCP stays on the Mediablock and shows as normal again.')) return;
        const promise = dispatch(restoreContent(modal.content._id)).unwrap();
        toast.promise(promise, {
            pending: 'Restoring DCP...',
            success: 'Removal request cleared — the DCP shows as normal again',
            error: {
                render({ data }) {
                    return data?.message || 'Error restoring content';
                }
            },
        });
        try {
            const updated = await promise;
            setModal(prevState => ({ ...prevState, content: updated }));
        } catch (error) {
            console.error('Restore error:', error);
        }
    };

    const handleTogglePinned = async e => {
        e.preventDefault();
        const promise = dispatch(togglePinnedContent(modal.content._id)).unwrap();
        toast.promise(promise, {
            pending: 'Updating',
            success: 'Updated successfully',
            error: 'Error updating content',
        });
        const content = await promise;
        setModal(prevState => ({
            ...prevState,
            content,
        }));
    };

    useEffect(() => {
        if (!user) return;
        let orgId = organizationId;
        if (organizationId === 'own' || !organizationId) {
            orgId = user.parentOrganization._id;
        }
        dispatch(fetchOrganization({token, orgId}));
        dispatch(fetchOrganizationContent({token, orgId}));
    }, [token, dispatch, organizationId, user]);

    // One card per film, built once per payload. Filters then keep only the members that match
    // (a film with nothing left disappears); the search picks whole films.
    const groups = React.useMemo(() => buildContentGroups(organizationContent), [organizationContent]);
    const visibleGroups = React.useMemo(
        () => filterContentGroups(groups, filterBy, isAdmin(user)).filter(group => groupMatchesQuery(group, filterBy.search)),
        [groups, filterBy, user]
    );
    const filtersActive = hasActiveFilters(filterBy) || filterBy.search.length > 0;
    // A line click opens the copy on the only location being filtered on, else the Mediablock one.
    const preferredLocation = filterBy.location.length === 1 ? filterBy.location[0] : 'mediablock';
    const visibleCounts = React.useMemo(() => visibleGroups.reduce((sum, group) => ({
        packages: sum.packages + group.counts.packages,
        keys: sum.keys + group.counts.keys,
    }), {packages: 0, keys: 0}), [visibleGroups]);

    const mediablockCplUuids = React.useMemo(
        () => new Set(
            organizationContent
                .filter(c => c.location === 'mediablock' && c.type === 'CPL' && c.cpl_uuid)
                .map(c => c.cpl_uuid)
        ),
        [organizationContent]
    );

    const isContentOnMediablock = (content) =>
        content?.type === 'CPL' && mediablockCplUuids.has(content?.cpl_uuid);

    // The lifecycle counts come from the grouped packages (one per CPL, duplicates folded), so a chip
    // always says exactly how many packages its filter will show. Removals the cleanup job has given
    // up on are counted apart from the ones still moving: they never resolve by themselves.
    const mediablockStats = React.useMemo(() => {
        const onMediablock = organizationContent.filter(c => c.location === 'mediablock');
        const states = countStates(groups);
        return {
            total: onMediablock.length,
            cpls: onMediablock.filter(c => c.type === 'CPL').length,
            pendingDeletion: states['pending-deletion'],
            deletionFailed: states['removal-failed'],
            transferring: states.transferring,
        };
    }, [organizationContent, groups]);

    const modalAlreadyOnMediablock = modal.content ? isContentOnMediablock(modal.content) : false;
    const modalIngestFailed = !!modal.content?.ingestFailedAt && !modalAlreadyOnMediablock;
    const modalIngesting = !!modal.content?.ingestRequestedAt && !modalIngestFailed && !modalAlreadyOnMediablock;
    const modalIngestBlocked = mediablockOffline && !modalAlreadyOnMediablock && !modalIngesting;

    return organization && (
        <div className="page px-4 py-3 d-flex flex-column overflow-y-auto">
            {/* Everything down to the counts stays pinned, so search and filters are always at hand
                while scrolling through a long list of DCPs. */}
            <div className="content-toolbar">
                {organizationId && (
                    <NavLink to={`/organizations/${organizationId}`} className="text-decoration-none">
                        <span className="fas fa-chevron-left me-2"/> {organization.name}
                    </NavLink>
                )}
                <PageHeader
                    title="content"
                    searchBar={{searchValue: filterBy.search, handleSearch}}
                    shrinkOnScroll={false}
                />

                <div className="d-flex flex-wrap justify-content-start align-items-center gap-3 mb-3">
                    <FiltersLabel active={filtersActive} onClear={clearFilters}/>

                    <ButtonGroup className="gap-2 flex-wrap">
                        {FILTER_CHIPS.map(chip => (
                            <Button
                                key={`${chip.facet}-${chip.value || ''}`}
                                color="primary"
                                outline={!isChipActive(chip)}
                                size="sm"
                                className="rounded-3"
                                onClick={() => toggleChip(chip)}
                            >
                                <span className={`fas ${isChipActive(chip) ? 'fa-toggle-on text-success' : chip.icon} me-2`}/>
                                {chip.label}
                            </Button>
                        ))}
                    </ButtonGroup>
                </div>

                <MediablockStatusBar
                    mediablock={organization.mediablock}
                    stats={mediablockStats}
                    lastSynced={organization.lastSynced}
                    activeStates={filterBy.state}
                    onToggleState={state => toggleChip({facet: 'state', value: state})}
                />

                <div className="pt-3 pb-2">
                    <span className="fas fa-list me-2 text-primary"/>
                    {[
                        `${visibleGroups.length} film${visibleGroups.length === 1 ? '' : 's'}`,
                        visibleCounts.packages > 0 && `${visibleCounts.packages} package${visibleCounts.packages === 1 ? '' : 's'}`,
                        isAdmin(user) && visibleCounts.keys > 0 && `${visibleCounts.keys} key${visibleCounts.keys === 1 ? '' : 's'}`,
                    ].filter(Boolean).join(' · ')}
                </div>
            </div>

            <div className="mb-3 mt-2 contentContainer">
                {visibleGroups.map(group => (
                    <ContentGroupCard
                        key={group.key}
                        group={group}
                        preferredLocation={preferredLocation}
                        toggleContentModal={toggleContentModal}
                        mediablockSerialNumber={organization?.mediablock?.serialNumber}
                    />
                ))}

                {visibleGroups.length === 0 && (
                    <div className="d-flex flex-column align-items-center py-5">
                        <span className="fas fa-inbox fa-3x text-white-50 mb-3 mt-5 pt-5"/>
                        {filtersActive ? (
                            <Fragment>
                                <p className="text-white-50 mb-3">No content matches your filters</p>
                                <Button color="primary" outline onClick={clearFilters}>Clear filters</Button>
                            </Fragment>
                        ) : (
                            <p className="text-white-50 text-uppercase">No content yet</p>
                        )}
                    </div>
                )}
            </div>

            {modal.isOpen && (
                <Modal isOpen={modal.isOpen} toggle={toggleContentModal()} size="lg" modalClassName="mw-100" scrollable>
                    <ModalHeader toggle={toggleContentModal()} className="text-primary">
                        <span className="fas fa-file me-2"/>
                        {modal.content.type.toUpperCase()} information
                    </ModalHeader>
                    <ModalBody>
                        <div className="cpl-details">
                            {cplInfo.map(renderField)}
                        </div>
                    </ModalBody>
                    <ModalFooter className="d-flex justify-content-between align-items-center gap-2">
                        {/* Only DCPs: the Mediablock cleanup job never claims KDMs, so offering to
                            mark one would leave it flagged forever with nothing acting on it. */}
                        {modal.content.location === 'mediablock' && modal.content.type === 'CPL' && (
                            <div className="d-flex flex-column align-items-start gap-1">
                                <Button
                                    color="danger"
                                    outline
                                    disabled={!canMarkForDeletion(modal.content, organization)}
                                    title={deletion?.detail || (mediablockOffline ? MEDIABLOCK_OFFLINE_REASON : undefined)}
                                    onClick={handleMarkDeleted}
                                >
                                    <span className={`fas ${deletion ? deletion.icon : mediablockOffline ? 'fa-plug-circle-xmark' : 'fa-trash'} me-2`}/>
                                    {deletion ? deletion.label : 'Delete from Mediablock'}
                                </Button>
                                {/* The cleanup job would fail three times and give up, so the request
                                    is refused up front (the server answers 409 for the same reason). */}
                                {!deletion && mediablockOffline && (
                                    <span className="small text-white-50" style={{maxWidth: '18rem'}}>{MEDIABLOCK_OFFLINE_REASON}</span>
                                )}
                            </div>
                        )}

                        {modal.content.location === 'mediablock' && canRestoreDeletion(modal.content) && (
                            <Button color="warning" outline onClick={handleRestore}
                                    title="Clears the removal request; the DCP stays on the Mediablock">
                                <span className="fas fa-rotate-left me-2"/> Restore
                            </Button>
                        )}

                        {modal.content.location === 'BAC_server' && (
                            <div className="d-flex flex-column align-items-start gap-1">
                                <Button
                                    color="primary"
                                    outline
                                    disabled={modalAlreadyOnMediablock || modalIngesting || modalIngestBlocked}
                                    title={
                                        modalAlreadyOnMediablock ? 'Already on Mediablock'
                                            : modalIngesting ? 'Transferring to Mediablock…'
                                                : modalIngestBlocked ? MEDIABLOCK_OFFLINE_TRANSFER_REASON
                                                    : modalIngestFailed ? (modal.content.ingestError || 'Previous ingest failed')
                                                        : undefined
                                    }
                                    onClick={() => {
                                        if (modalAlreadyOnMediablock || modalIngesting || modalIngestBlocked) return;
                                        handleIngest(modal.content);
                                    }}
                                >
                                    <span className={`fas ${
                                        modalAlreadyOnMediablock ? 'fa-check'
                                            : modalIngesting ? 'fa-spinner'
                                                : modalIngestBlocked ? 'fa-plug-circle-xmark'
                                                    : modalIngestFailed ? 'fa-rotate-right'
                                                        : 'fa-upload'
                                    } me-2`}/>
                                    {modalAlreadyOnMediablock ? 'Already on Mediablock'
                                        : modalIngesting ? 'Transferring to Mediablock…'
                                            : modalIngestFailed ? 'Retry transfer'
                                                : 'Transfer CPL to Mediablock'}
                                </Button>
                                {modalIngestBlocked && (
                                    <span className="small text-white-50" style={{maxWidth: '18rem'}}>{MEDIABLOCK_OFFLINE_TRANSFER_REASON}</span>
                                )}
                            </div>
                        )}

                        {(modal.content.isPinned || (modal.content.type === 'CPL' && modal.content.movieVersion)) && (
                            <Button color="primary" onClick={handleTogglePinned}>
                                {modal.content.isPinned ? (
                                    <Fragment>
                                        <span className="fas fa-thumbtack-slash me-2 fa-fw" /> Unpin content
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <span className="fas fa-thumbtack me-2 fa-fw" /> Pin content
                                    </Fragment>
                                )}
                            </Button>
                        )}

                        <Button color="primary" outline onClick={toggleContentModal()}>Close</Button>
                    </ModalFooter>
                </Modal>
            )}
        </div>
    );
};

export default ContentManager;
